import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FlightAlertsHeader } from "./FlightAlertsHeader";
import { HkiaAttribution } from "./HkiaAttribution";
import { AlertListRow } from "./AlertListRow";
import { SpinnerIcon } from "./icons";
import { StopConfirmationDialog } from "./StopConfirmationDialog";
import { useMyFlightAlerts } from "./handler/useMyFlightAlerts";
import { useStopFlightAlert } from "./handler/useStopFlightAlert";
import { MeFlightAlertItem } from "../../api/meFlightAlerts.service";

/**
 * "Alert list" (DASH-6345 UI) -- every flight alert under the signed-in user's number, with its
 * state. Reached today by direct navigation; the Flight lookup sign-in link that's meant to land
 * here is a separate, not-yet-built piece (Flight Alerts Phase 2, step 4).
 */
const MyFlightAlerts: React.FC = () => {
  const { t } = useTranslation("flightAlerts");
  const { data, isLoading, isError, isAuthError, refetch } =
    useMyFlightAlerts();
  const { stop, isStopping } = useStopFlightAlert();

  const [pendingStop, setPendingStop] = useState<MeFlightAlertItem | null>(
    null,
  );
  const [stopErrorMessage, setStopErrorMessage] = useState<string | null>(null);

  const handleCancelStop = () => {
    setPendingStop(null);
    setStopErrorMessage(null);
  };

  const handleConfirmStop = async () => {
    if (!pendingStop) return;
    setStopErrorMessage(null);
    const outcome = await stop(pendingStop.id);
    if (!outcome.success) {
      setStopErrorMessage(t("stopConfirmation.errFailed"));
      return;
    }
    // A fresh stop and an already-closed race (Flow step 4: "closed itself in the meantime ->
    // told it had already closed, not an error") both land here -- either way, the row's true
    // current state is whatever the server now says, so refetch rather than guess at it locally.
    setPendingStop(null);
    refetch();
  };

  return (
    // Same outer shell and 600px content width as the Flight lookup wizard (FlightAlerts.tsx) --
    // this page was originally built with its own narrower 480px/px-4 layout, which made the
    // Alert list visibly narrower than Flight lookup when navigating between the two (feedback
    // from Roy Chan, 2026-09-24: "the theme and width doesn't really align with existing
    // screen"). There's no Figma for Phase 2 to match instead (confirmed: epic UI section is
    // still `[WIP: Roy Chan — Figma link; no frames exist yet]`), so matching the already-shipped
    // Phase 1 screen is the closest thing to an approved reference.
    <div className="min-h-screen bg-app flex flex-col items-center px-6 pt-[74px] pb-6 md:pt-20 md:pb-20">
      <div className="w-full max-w-[600px] flex flex-col gap-5 md:gap-6">
        <FlightAlertsHeader />

        <h1 className="font-lexend text-[24px]/[28px] font-bold tracking-[-0.8px] text-ink-100">
          {t("alertList.heading")}
        </h1>

        {/* Not shown for the empty state below -- that state already carries its own,
          differently-worded link to Flight lookup ("Look up a flight to set one up"). This one
          is for a user who already holds alerts and wants to add another, which the epic's own
          empty-state precedent implies but never states for the non-empty case. */}
        {!isLoading && !isAuthError && data && data.length > 0 && (
          <Link
            to="/flight-alerts"
            className="w-fit font-lexend text-[14px]/[18px] font-bold tracking-normal text-brand-blue"
          >
            {t("alertList.lookupAnotherFlightLink")}
          </Link>
        )}

        {isLoading && (
          <div
            role="status"
            aria-label={t("alertList.loading")}
            className="flex flex-1 items-center justify-center py-16"
          >
            <SpinnerIcon className="h-8 w-8 animate-spin text-brand-blue" />
          </div>
        )}

        {isAuthError && (
          <div className="flex flex-col gap-3 rounded-2xl border-2 border-ink-12 bg-card p-6">
            <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-100">
              {t("alertList.signInRequired")}
            </p>
            <Link
              to="/flight-alerts"
              className="w-fit font-lexend text-[14px]/[18px] font-bold tracking-normal text-brand-blue"
            >
              {t("alertList.signInRequiredLink")}
            </Link>
          </div>
        )}

        {isError && !isAuthError && (
          <div className="flex flex-col gap-3 rounded-lg bg-flight-red-bg p-4">
            <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-flight-red">
              {t("alertList.loadFailed")}
            </p>
            <button
              type="button"
              onClick={refetch}
              className="w-fit font-lexend text-[14px]/[18px] font-bold tracking-normal text-flight-red underline"
            >
              {t("alertList.retry")}
            </button>
          </div>
        )}

        {!isLoading &&
          !isError &&
          !isAuthError &&
          data &&
          data.length === 0 && (
            <div className="flex flex-col gap-3 rounded-2xl border-2 border-ink-12 bg-card p-6">
              <p className="font-lexend text-[16px]/[20px] font-bold tracking-[-0.1px] text-ink-100">
                {t("alertList.emptyHeading")}
              </p>
              <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60">
                {t("alertList.emptyBody")}
              </p>
              <Link
                to="/flight-alerts"
                className="w-fit font-lexend text-[14px]/[18px] font-bold tracking-normal text-brand-blue"
              >
                {t("alertList.emptyLink")}
              </Link>
            </div>
          )}

        {!isLoading && !isError && !isAuthError && data && data.length > 0 && (
          <div className="flex flex-col gap-4">
            {data.map((alert) => (
              <AlertListRow
                key={alert.id}
                alert={alert}
                onStopClick={setPendingStop}
              />
            ))}
          </div>
        )}

        {!isLoading && !isAuthError && <HkiaAttribution />}

        {pendingStop && (
          <StopConfirmationDialog
            alert={pendingStop}
            isStopping={isStopping}
            errorMessage={stopErrorMessage}
            onConfirm={handleConfirmStop}
            onCancel={handleCancelStop}
          />
        )}
      </div>
    </div>
  );
};

export default MyFlightAlerts;
